/**
 * @fileoverview
 * Token Service - Manages JWT tokens
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable } from '@angular/core';
import { StorageUtil } from '../../utils';
import { STORAGE_KEYS } from '../../constants';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  /**
   * Get access token from storage
   */
  getToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Save access token to storage
   */
  setToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Save refresh token to storage
   */
  setRefreshToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Get CSRF token from storage
   */
  getCsrfToken(): string | null {
    return StorageUtil.getLocal<string>(STORAGE_KEYS.CSRF_TOKEN);
  }

  /**
   * Save CSRF token to storage
   */
  setCsrfToken(token: string): void {
    StorageUtil.setLocal(STORAGE_KEYS.CSRF_TOKEN, token);
  }

  /**
   * Remove tokens from storage
   */
  removeTokens(): void {
    StorageUtil.removeLocal(STORAGE_KEYS.ACCESS_TOKEN);
    StorageUtil.removeLocal(STORAGE_KEYS.REFRESH_TOKEN);
    StorageUtil.removeLocal(STORAGE_KEYS.CSRF_TOKEN);
  }

  /**
   * Check if token exists
   */
  hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Decode JWT token (without verification)
   */
  decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.getToken();
    
    if (!tokenToCheck) {
      return true;
    }

    const decoded = this.decodeToken(tokenToCheck);
    
    if (!decoded || !decoded.exp) {
      return true;
    }

    const expirationDate = new Date(decoded.exp * 1000);
    const now = new Date();
    
    return expirationDate <= now;
  }

  /**
   * Get user ID from token
   */
  getUserIdFromToken(token?: string): number | null {
    const tokenToCheck = token || this.getToken();
    
    if (!tokenToCheck) {
      return null;
    }

    const decoded = this.decodeToken(tokenToCheck);
    return decoded?.sub || null;
  }
}

